import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Play,
  Video,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Loader2,
  XCircle,
  Download
} from 'lucide-react';
import { api } from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';
import VideoPlayer from '../components/VideoPlayer';
import Button from '../components/Button';
import { formatSessionTicketContext, type SessionLinkedTicket } from '../utils/ticketDisplay';
import {
  buildTempStreamPath,
  getSourceClips,
  normalizeSessionVideo,
  resolveFinalVideo,
} from '../utils/sessionVideos';

interface SessionVideo {
  id: string;
  filename: string;
  originalFilename: string;
  fileSize: number;
  durationSeconds: number;
  resolution: string;
  format: string;
  createdAt: string;
}

interface AnxietyData {
  id: string;
  timestampSeconds: number;
  anxietyLevel: number;
  heartRate?: number;
  notes?: string;
  createdAt: string;
}

interface SessionDetailData {
  id: string;
  phobiaTypeId?: string | null;
  phobiaTypeName?: string | null;
  phobiaTypeDescription?: string | null;
  ticket?: SessionLinkedTicket | null;
  title: string;
  description?: string;
  sessionDate: string;
  durationMinutes?: number;
  anxietyLevelStart?: number;
  anxietyLevelEnd?: number;
  notes?: string;
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
  videos: SessionVideo[];
  anxietyData: AnxietyData[];
}

const SessionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedVideo, setSelectedVideo] = useState<SessionVideo | null>(null);
  const [selectedStreamPath, setSelectedStreamPath] = useState<string | undefined>();

  const { data: session, isLoading, error } = useQuery<SessionDetailData>(
    ['session', id],
    async () => {
      try {
        console.log('Fetching session detail for ID:', id);
        const response = await api.get(`/sessions/${id}`);
        console.log('Session detail response:', response.data);
        return response.data;
      } catch (error: any) {
        console.error('Error fetching session:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        throw error;
      }
    },
    {
      enabled: !!id,
      retry: 1,
    }
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'planned':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return CheckCircle;
      case 'in_progress':
        return Loader2;
      case 'planned':
        return Calendar;
      case 'cancelled':
        return XCircle;
      default:
        return AlertCircle;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    if (mb < 1) {
      return `${(mb * 1024).toFixed(0)} KB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  const getVideoUrl = (video: SessionVideo) => {
    return `/api/videos/${video.id}/stream`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !session) {
    // Safely extract error message, handling cases where error might be an object
    let errorMessage = 'Session not found';
    
    if (error) {
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (typeof error === 'object') {
        // Handle error.response?.data?.error which might be a string or object
        const errorData = (error as any).response?.data;
        if (errorData) {
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          } else if (typeof errorData.error === 'object' && errorData.error?.message) {
            errorMessage = errorData.error.message;
          } else if (typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          } else if (typeof (error as any).message === 'string') {
            errorMessage = (error as any).message;
          }
        } else if (typeof (error as any).message === 'string') {
          errorMessage = (error as any).message;
        }
      }
    }
    
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="card bg-red-50 border-red-200">
          <div className="card-content">
            <div className="flex items-center space-x-2 text-red-800">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Session not found</span>
            </div>
            <p className="mt-2 text-red-600">
              {errorMessage}
            </p>
            <p className="mt-2 text-sm text-red-500">
              Session ID: {id}
            </p>
            <p className="mt-2 text-sm text-gray-600">
              The session you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <Link to="/sessions" className="mt-4 inline-block">
              <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" />}>
                Back to Sessions
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(session.status);
  const finalVideo = resolveFinalVideo(session.videos, session.ticket);
  const sourceClips =
    session.status === 'completed' && finalVideo
      ? getSourceClips(session.videos)
      : session.videos;

  const playVideo = (video: SessionVideo, streamPath?: string) => {
    setSelectedStreamPath(streamPath);
    setSelectedVideo(video);
  };

  const renderVideoRow = (video: SessionVideo, streamPath?: string, highlight?: boolean) => (
    <div
      key={video.id}
      className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
        highlight ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 hover:bg-gray-100'
      }`}
    >
      <div className="flex items-center flex-1 min-w-0">
        <Video className={`h-5 w-5 mr-3 flex-shrink-0 ${highlight ? 'text-emerald-600' : 'text-blue-600'}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {video.originalFilename || video.filename}
          </p>
          <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
            <span>{formatDuration(video.durationSeconds)}</span>
            <span>•</span>
            <span>{video.resolution}</span>
            <span>•</span>
            <span>{formatFileSize(video.fileSize)}</span>
            <span>•</span>
            <span>{formatDate(video.createdAt)}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-2 ml-4">
        <button
          onClick={() => playVideo(video, streamPath)}
          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
          title="Play video"
        >
          <Play className="h-5 w-5" />
        </button>
        <button
          onClick={async () => {
            try {
              const token = localStorage.getItem('token');
              if (!token) {
                alert('Please log in to download videos');
                return;
              }
              const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
              const downloadUrl = streamPath
                ? `${apiBaseUrl}/${streamPath.replace(/^\//, '')}`
                : `${apiBaseUrl}/videos/${video.id}/download`;
              const response = await fetch(downloadUrl, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download =
                (video.originalFilename && video.originalFilename.replace(/\.webm$/i, '.mp4')) ||
                `video-${video.id}.mp4`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            } catch (error: unknown) {
              const message = error instanceof Error ? error.message : 'Please try again';
              alert('Failed to download video: ' + message);
            }
          }}
          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
          title="Download video"
        >
          <Download className="h-5 w-5" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <Link to="/sessions" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">Back to Sessions</span>
          <span className="sm:hidden">Back</span>
        </Link>
        
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{session.title}</h1>
            {session.description && (
              <p className="mt-2 text-sm sm:text-base text-gray-600">{session.description}</p>
            )}
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(session.status)} self-start sm:self-auto`}>
            <StatusIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
            {session.status.charAt(0).toUpperCase() + session.status.slice(1).replace('_', ' ')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="md:col-span-2 space-y-6">
          {/* Session Information */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">Session Information</h2>
            </div>
            <div className="card-content space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {formatSessionTicketContext(session.ticket) ? (
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium text-gray-500">Room booking</label>
                    <p className="mt-1 text-gray-900">{formatSessionTicketContext(session.ticket)}</p>
                    {session.ticket?.customerName && (
                      <p className="mt-1 text-sm text-gray-600">Customer: {session.ticket.customerName}</p>
                    )}
                  </div>
                ) : session.phobiaTypeName ? (
                  <div>
                    <label className="text-sm font-medium text-gray-500">ETR Type (legacy)</label>
                    <p className="mt-1 text-gray-900">{session.phobiaTypeName}</p>
                    {session.phobiaTypeDescription && (
                      <p className="mt-1 text-sm text-gray-600">{session.phobiaTypeDescription}</p>
                    )}
                  </div>
                ) : null}
                <div>
                  <label className="text-sm font-medium text-gray-500">Session Date</label>
                  <p className="mt-1 text-gray-900">{formatDate(session.sessionDate)}</p>
                </div>
                {session.durationMinutes && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Duration</label>
                    <p className="mt-1 text-gray-900">{session.durationMinutes} minutes</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">Videos</label>
                  <p className="mt-1 text-gray-900">{session.videos.length} video(s)</p>
                </div>
              </div>

              {/* Anxiety Levels */}
              {(session.anxietyLevelStart || session.anxietyLevelEnd) && (
                <div className="pt-4 border-t border-gray-200">
                  <label className="text-sm font-medium text-gray-500 mb-3 block">Anxiety Levels</label>
                  <div className="flex items-center space-x-4">
                    {session.anxietyLevelStart && (
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Start</span>
                          <span className="text-sm font-medium text-gray-900">{session.anxietyLevelStart}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-red-500 h-2 rounded-full"
                            style={{ width: `${(session.anxietyLevelStart / 10) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {session.anxietyLevelEnd && (
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">End</span>
                          <span className="text-sm font-medium text-gray-900">{session.anxietyLevelEnd}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${(session.anxietyLevelEnd / 10) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {session.anxietyLevelStart && session.anxietyLevelEnd && (
                    <p className="mt-2 text-sm text-gray-600">
                      {session.anxietyLevelEnd < session.anxietyLevelStart ? (
                        <span className="text-green-600">Anxiety decreased by {session.anxietyLevelStart - session.anxietyLevelEnd} points</span>
                      ) : session.anxietyLevelEnd > session.anxietyLevelStart ? (
                        <span className="text-yellow-600">Anxiety increased by {session.anxietyLevelEnd - session.anxietyLevelStart} points</span>
                      ) : (
                        <span>Anxiety level remained the same</span>
                      )}
                    </p>
                  )}
                </div>
              )}

              {/* Notes */}
              {session.notes && (
                <div className="pt-4 border-t border-gray-200">
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Notes</label>
                  <div className="bg-gray-50 rounded-md p-4">
                    <p className="text-gray-900 whitespace-pre-wrap">{session.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Videos */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">
                {finalVideo && session.status === 'completed' ? 'Final video' : 'Videos'}
              </h2>
              <span className="text-sm text-gray-500">
                {finalVideo && session.status === 'completed'
                  ? `${sourceClips.length} source clip(s)`
                  : `${session.videos.length} video(s)`}
              </span>
            </div>
            <div className="card-content">
              {session.videos.length > 0 || finalVideo ? (
                <div className="space-y-3">
                  {finalVideo &&
                    session.status === 'completed' &&
                    renderVideoRow(
                      normalizeSessionVideo(finalVideo),
                      buildTempStreamPath(finalVideo.filename),
                      true
                    )}
                  {finalVideo && session.status === 'completed' && sourceClips.length > 0 && (
                    <p className="text-sm font-medium text-gray-600 pt-2">Source clips</p>
                  )}
                  {sourceClips.map((video) => renderVideoRow(normalizeSessionVideo(video)))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Video className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p>No videos uploaded for this session</p>
                </div>
              )}
            </div>
          </div>

          {/* Anxiety Tracking Data */}
          {session.anxietyData && session.anxietyData.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 className="text-xl font-semibold text-gray-900">Anxiety Tracking</h2>
                <span className="text-sm text-gray-500">{session.anxietyData.length} data point(s)</span>
              </div>
              <div className="card-content">
                <div className="space-y-3">
                  {session.anxietyData.map((data) => (
                    <div
                      key={data.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {formatDuration(data.timestampSeconds)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <TrendingUp className="h-4 w-4 text-red-400" />
                          <span className="text-sm font-medium text-gray-900">
                            Anxiety: {data.anxietyLevel}/10
                          </span>
                        </div>
                        {data.heartRate && (
                          <div className="text-sm text-gray-600">
                            Heart Rate: {data.heartRate} bpm
                          </div>
                        )}
                      </div>
                      {data.notes && (
                        <p className="text-sm text-gray-600">{data.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Session Metadata */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Metadata</h2>
            </div>
            <div className="card-content space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Created</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(session.createdAt)}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Last Updated</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(session.updatedAt)}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Session ID</label>
                <p className="mt-1 text-sm text-gray-500 font-mono break-all">{session.id}</p>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Quick Stats</h2>
            </div>
            <div className="card-content space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Videos</span>
                <span className="text-sm font-medium text-gray-900">{session.videos.length}</span>
              </div>
              {session.videos.length > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Duration</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDuration(session.videos.reduce((sum, v) => sum + v.durationSeconds, 0))}
                  </span>
                </div>
              )}
              {session.anxietyData && session.anxietyData.length > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Tracking Points</span>
                  <span className="text-sm font-medium text-gray-900">{session.anxietyData.length}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 truncate flex-1">
                {selectedVideo.originalFilename || selectedVideo.filename}
              </h3>
              <button
                onClick={() => {
                  setSelectedVideo(null);
                  setSelectedStreamPath(undefined);
                }}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <VideoPlayer
                video={{
                  id: selectedVideo.id,
                  filename: selectedVideo.filename,
                  originalFilename: selectedVideo.originalFilename,
                  filePath: getVideoUrl(selectedVideo),
                  fileSize: selectedVideo.fileSize,
                  durationSeconds: selectedVideo.durationSeconds,
                  resolution: selectedVideo.resolution,
                  format: selectedVideo.format,
                  createdAt: selectedVideo.createdAt
                }}
                streamPath={selectedStreamPath}
                onClose={() => {
                  setSelectedVideo(null);
                  setSelectedStreamPath(undefined);
                }}
                isModal={true}
              />
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-gray-500">Duration</label>
                  <p className="text-gray-900">{formatDuration(selectedVideo.durationSeconds)}</p>
                </div>
                <div>
                  <label className="text-gray-500">Resolution</label>
                  <p className="text-gray-900">{selectedVideo.resolution}</p>
                </div>
                <div>
                  <label className="text-gray-500">File Size</label>
                  <p className="text-gray-900">{formatFileSize(selectedVideo.fileSize)}</p>
                </div>
                <div>
                  <label className="text-gray-500">Format</label>
                  <p className="text-gray-900">{selectedVideo.format.toUpperCase()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionDetail;